/**
 * Notifications Service
 * Handles notifications and @mentions functionality
 */

import { supabase } from '../lib/supabase'
import type {
  Notification,
  CreateNotificationData,
  MentionableUser,
  ParsedMention
} from '../types/notification'

/**
 * Get notifications for the current user
 */
export async function getNotifications(
  limit: number = 20,
  includeRead: boolean = false
): Promise<Notification[]> {
  let query = supabase
    .from('notifications')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit)

  if (!includeRead) {
    query = query.eq('is_read', false)
  }

  const { data, error } = await query

  if (error) {
    console.error('Error fetching notifications:', error)
    return []
  }

  return data || []
}

/**
 * Get unread notification count for the current user
 */
export async function getUnreadCount(): Promise<number> {
  const { count, error } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('is_read', false)

  if (error) {
    console.error('Error fetching unread count:', error)
    return 0
  }

  return count || 0
}

/**
 * Mark a notification as read
 */
export async function markAsRead(
  notificationId: string
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('id', notificationId)

  if (error) {
    console.error('Error marking notification as read:', error)
    return { success: false, error: 'Kunde inte markera som läst' }
  }

  return { success: true }
}

/**
 * Mark all notifications as read for the current user
 */
export async function markAllAsRead(): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('is_read', false)

  if (error) {
    console.error('Error marking all notifications as read:', error)
    return { success: false, error: 'Kunde inte markera alla som lästa' }
  }

  return { success: true }
}

/**
 * Delete a notification
 */
export async function deleteNotification(
  notificationId: string
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('notifications')
    .delete()
    .eq('id', notificationId)

  if (error) {
    console.error('Error deleting notification:', error)
    return { success: false, error: 'Kunde inte ta bort notifikation' }
  }

  return { success: true }
}

/**
 * Create a notification
 */
export async function createNotification(
  data: CreateNotificationData
): Promise<{ success: boolean; notification?: Notification; error?: string }> {
  const { data: notification, error } = await supabase
    .from('notifications')
    .insert(data)
    .select()
    .single()

  if (error) {
    console.error('Error creating notification:', error)
    return { success: false, error: 'Kunde inte skapa notifikation' }
  }

  return { success: true, notification }
}

/**
 * Create mention notifications for users mentioned in a comment
 */
export async function createMentionNotifications(
  mentions: ParsedMention[],
  commentId: string,
  entityType: string,
  entityId: string,
  triggeredByUserId: string,
  triggeredByName: string
): Promise<void> {
  if (mentions.length === 0) return

  // Build link based on entity type
  const linkMap: Record<string, string> = {
    assignment: `/admin/assignments/${entityId}`,
    customer: `/admin/customers/${entityId}`,
    subcontractor: `/admin/subcontractors/${entityId}`,
    article: `/admin/articles/${entityId}`
  }

  const link = linkMap[entityType] || '/'

  // Create notifications for each mentioned user
  const notifications = mentions.map(mention => ({
    user_id: mention.userId,
    type: 'mention' as const,
    title: `${triggeredByName} nämnde dig i en kommentar`,
    message: `Du blev nämnd i en kommentar`,
    link,
    source_type: 'comment',
    source_id: commentId,
    triggered_by_user_id: triggeredByUserId,
    triggered_by_name: triggeredByName
  }))

  // Insert all notifications
  const { error } = await supabase
    .from('notifications')
    .insert(notifications)

  if (error) {
    console.error('Error creating mention notifications:', error)
  }

  // Also save the mentions to comment_mentions table
  const mentionRecords = mentions.map(mention => ({
    comment_id: commentId,
    mentioned_user_id: mention.userId
  }))

  const { error: mentionError } = await supabase
    .from('comment_mentions')
    .insert(mentionRecords)

  if (mentionError) {
    console.error('Error saving comment mentions:', mentionError)
  }

  // Send push notifications to mentioned users
  try {
    const userIds = mentions.map(m => m.userId)

    const { error: pushError } = await supabase.functions.invoke('send-push', {
      body: {
        userIds,
        payload: {
          title: `${triggeredByName} nämnde dig`,
          body: 'Du blev nämnd i en kommentar',
          url: link,
          tag: 'mention'
        }
      }
    })

    if (pushError) {
      console.error('Error sending push notifications for mentions:', pushError)
    }
  } catch (pushError) {
    console.error('Error sending push notifications:', pushError)
  }
}

/**
 * Get users that can be mentioned (for autocomplete)
 * @param searchTerm - Text to filter by name/email
 * @param assignmentId - Optional assignment ID (required for TGs to see colleagues)
 */
export async function getMentionableUsers(
  searchTerm: string = '',
  assignmentId?: string
): Promise<MentionableUser[]> {
  const { data, error } = await supabase
    .rpc('get_mentionable_users', {
      search_term: searchTerm,
      p_assignment_id: assignmentId || null
    })

  if (error) {
    console.error('Error fetching mentionable users:', error)

    // Fallback: query user_profiles directly
    const { data: fallbackData, error: fallbackError } = await supabase
      .from('user_profiles')
      .select('user_id, full_name, email, role')
      .eq('is_active', true)
      .or(`full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`)
      .order('full_name')
      .limit(10)

    if (fallbackError) {
      console.error('Fallback query also failed:', fallbackError)
      return []
    }

    return fallbackData || []
  }

  return data || []
}

/**
 * Parse @mentions from comment content
 * Returns an array of ParsedMention objects
 */
export function parseMentions(
  content: string,
  users: MentionableUser[]
): ParsedMention[] {
  const mentions: ParsedMention[] = []

  // Match @username patterns (handles names with spaces using quotes or simple names)
  // Pattern 1: @"Full Name" (with quotes)
  // Pattern 2: @FirstName (single word)
  const mentionRegex = /@"([^"]+)"|@(\S+)/g

  let match
  while ((match = mentionRegex.exec(content)) !== null) {
    const mentionedName = match[1] || match[2] // Either quoted name or simple name

    // Find matching user
    const user = users.find(u =>
      u.full_name.toLowerCase() === mentionedName.toLowerCase() ||
      u.full_name.toLowerCase().startsWith(mentionedName.toLowerCase())
    )

    if (user) {
      mentions.push({
        userId: user.user_id,
        userName: user.full_name,
        startIndex: match.index,
        endIndex: match.index + match[0].length
      })
    }
  }

  return mentions
}

/**
 * Subscribe to new notifications for the current user
 */
export function subscribeToNotifications(
  userId: string,
  callback: (notification: Notification) => void
) {
  const subscription = supabase
    .channel(`notifications:${userId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`
      },
      (payload) => {
        callback(payload.new as Notification)
      }
    )
    .subscribe()

  return () => {
    subscription.unsubscribe()
  }
}
